import React from "react";
import { Dropdown, Tag, Tooltip } from "antd";
import type { MenuProps } from "antd";
import { Link } from "react-router-dom";

import { all_routes } from "../../../router/all_routes";
import {
  PROJECT_PRIORITY_OPTIONS,
  STATUS_CATEGORY_COLORS,
  PROJECT_VISIBILITY_OPTIONS,
} from "../../constants/projects.constants";
import type { Project } from "../../../../core/data/redux/projects/types";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

interface ProjectCardProps {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
}

const PRIORITY_GRADIENT: Record<number, string> = {
  2: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
  1: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
  0: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)",
};

const STATUS_CONFIG: Record<number, { bg: string; color: string; dot: string; label: string }> = {
  1: { bg: "#ecfdf5", color: "#059669", dot: "#10b981", label: "Active" },
  0: { bg: "#fef2f2", color: "#dc2626", dot: "#ef4444", label: "Inactive" },
};

const priorityLabel = (priority: number | null | undefined) =>
  PROJECT_PRIORITY_OPTIONS.find((o) => o.value === priority)?.label || "—";

const visibilityLabel = (visibility: number | null | undefined) =>
  PROJECT_VISIBILITY_OPTIONS.find((o) => o.value === visibility)?.label || "Public";

const VISIBILITY_COLORS: Record<number, { bg: string, color: string, icon: string }> = {
  0: { bg: "#dcfce7", color: "#166534", icon: "ti-world" },
  1: { bg: "#f3e8ff", color: "#6b21a8", icon: "ti-lock" },
  2: { bg: "#ffe4e6", color: "#be123c", icon: "ti-shield-lock" },
};

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onEdit, onDelete }) => {
  const stats = project.stats || {
    total: 0,
    by_status_category: { TODO: 0, IN_PROGRESS: 0, DONE: 0 },
  };
  const total = stats.total || 0;
  const done = stats.by_status_category?.DONE || 0;
  const completionPct = total > 0 ? Math.round((done / total) * 100) : 0;
  const sc = STATUS_CONFIG[project.status ?? 1] || STATUS_CONFIG[1];

  const detailPath = all_routes.projectOverview.replace(":projectKey", project.key);

  const menuItems: MenuProps["items"] = [
    {
      key: "open",
      icon: <i className="ti ti-external-link" style={{ color: "#3b82f6" }} />,
      label: <Link to={detailPath}>Open project</Link>,
    },
    {
      key: "edit",
      icon: <i className="ti ti-edit" style={{ color: "#f59e0b" }} />,
      label: "Edit",
      onClick: () => onEdit(project),
    },
    { type: "divider" },
    {
      key: "delete",
      icon: <i className="ti ti-trash" />,
      danger: true,
      label: "Delete",
      onClick: () => onDelete(project),
    },
  ];

  return (
    <div
      className="card h-100 border-0 overflow-hidden"
      style={{
        borderRadius: 14,
        boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
        transition: "all 0.25s ease",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 12px 28px rgba(0,0,0,0.10)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 1px 8px rgba(0,0,0,0.06)";
      }}
    >
      {/* Gradient accent bar */}
      <div
        style={{
          height: 5,
          background: PRIORITY_GRADIENT[project.priority ?? 1] || PRIORITY_GRADIENT[1],
        }}
      />

      <div className="card-body bg-white d-flex flex-column" style={{ padding: "14px 16px 12px" }}>
        {/* Status + Actions */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Tooltip title={`Status: ${sc.label}`}>
            <span
              className="d-inline-flex align-items-center gap-1"
              style={{
                background: sc.bg,
                color: sc.color,
                fontSize: 11,
                fontWeight: 600,
                padding: "3px 10px",
                borderRadius: 20,
                textTransform: "capitalize",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: sc.dot,
                  display: "inline-block",
                }}
              />
              {sc.label}
            </span>
          </Tooltip>
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <button
              className="btn p-0"
              style={{ background: "none", border: "none", lineHeight: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Tooltip title="Actions">
                <i className="ti ti-dots-vertical" style={{ fontSize: 18, color: "#94a3b8" }} />
              </Tooltip>
            </button>
          </Dropdown>
        </div>

        {/* Title & Key */}
        <Link to={detailPath} className="text-decoration-none">
          <Tooltip title={project.name}>
            <h6
              className="mb-1 text-truncate"
              style={{ fontSize: 15, fontWeight: 650, color: "#0f172a", letterSpacing: -0.2 }}
            >
              {project.name}
            </h6>
          </Tooltip>
        </Link>
        <Tooltip title={`Key: ${project.key}`}>
          <p className="mb-2 text-truncate" style={{ fontSize: 12.5, color: "#64748b" }}>
            <i className="ti ti-hash me-1" style={{ fontSize: 13 }} />
            {project.key}
          </p>
        </Tooltip>

        {/* Metadata chips */}
        <div className="d-flex flex-wrap gap-1 mb-2">
          <Tooltip title={`Priority: ${priorityLabel(project.priority)}`}>
            <span
              className="d-inline-flex align-items-center"
              style={{
                background: "#eff6ff",
                padding: "3px 8px",
                borderRadius: 6,
                fontSize: 11,
                color: "#2563eb",
                fontWeight: 500,
              }}
            >
              <i className="ti ti-flag me-1" style={{ fontSize: 12 }} />
              {priorityLabel(project.priority)}
            </span>
          </Tooltip>
          <Tooltip title={`Visibility: ${visibilityLabel(project.visibility)}`}>
            {(() => {
              const vConfig = VISIBILITY_COLORS[project.visibility ?? 0] || VISIBILITY_COLORS[0];
              return (
                <span
                  className="d-inline-flex align-items-center"
                  style={{
                    background: vConfig.bg,
                    padding: "3px 8px",
                    borderRadius: 6,
                    fontSize: 11,
                    color: vConfig.color,
                    fontWeight: 500,
                  }}
                >
                  <i className={`ti ${vConfig.icon} me-1`} style={{ fontSize: 12 }} />
                  {visibilityLabel(project.visibility)}
                </span>
              );
            })()}
          </Tooltip>
          {project.start_date && (
            <Tooltip title={`Start: ${humanReadableDate(project.start_date)}`}>
              <span
                className="d-inline-flex align-items-center"
                style={{
                  background: "#f1f5f9",
                  padding: "3px 8px",
                  borderRadius: 6,
                  fontSize: 11,
                  color: "#475569",
                  fontWeight: 500,
                }}
              >
                <i className="ti ti-calendar me-1" style={{ fontSize: 12 }} />
                {humanReadableDate(project.start_date)}
              </span>
            </Tooltip>
          )}
          {project.end_date && (
            <Tooltip title={`End: ${humanReadableDate(project.end_date)}`}>
              <span
                className="d-inline-flex align-items-center"
                style={{
                  background: "#faf5ff",
                  padding: "3px 8px",
                  borderRadius: 6,
                  fontSize: 11,
                  color: "#7c3aed",
                  fontWeight: 500,
                }}
              >
                <i className="ti ti-calendar-due me-1" style={{ fontSize: 12 }} />
                {humanReadableDate(project.end_date)}
              </span>
            </Tooltip>
          )}
        </div>

        {/* Progress */}
        <Tooltip title={`${done} of ${total} issues done`}>
          <div className="mb-2 small text-muted" style={{ cursor: "help" }}>
            {total > 0 ? `${done} of ${total} issues done` : "No issues yet"}
          </div>
        </Tooltip>
        <div
          className="progress mb-1"
          style={{ height: 7, background: "#e5e7eb", borderRadius: 4 }}
        >
          <div
            className="progress-bar"
            role="progressbar"
            style={{
              width: `${completionPct}%`,
              background: STATUS_CATEGORY_COLORS.DONE,
              transition: "width 0.5s ease",
            }}
          />
        </div>

        {/* Status stats footer */}
        <div
          className="d-flex align-items-center justify-content-between mt-auto pt-2"
          style={{ borderTop: "2px solid #f1f5f9" }}
        >
          <StatChip
            label="To Do"
            value={stats.by_status_category?.TODO || 0}
            color="#6B7280"
          />
          <StatChip
            label="In Progress"
            value={stats.by_status_category?.IN_PROGRESS || 0}
            color="#3B82F6"
          />
          <StatChip
            label="Done"
            value={stats.by_status_category?.DONE || 0}
            color="#10B981"
          />
        </div>
      </div>
    </div>
  );
};

interface StatChipProps {
  label: string;
  value: number;
  color: string;
}

const StatChip: React.FC<StatChipProps> = ({ label, value, color }) => (
  <Tooltip title={`${label}: ${value}`}>
    <div className="text-center" style={{ cursor: "help" }}>
      <div className="fw-bold" style={{ color, fontSize: 15 }}>
        {value}
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>
        {label}
      </div>
    </div>
  </Tooltip>
);

export default ProjectCard;
